import React, { useEffect } from "react";
import { Sparkles, Dumbbell, Ticket, Star, Zap } from "lucide-react";

// Map config icon names -> lucide-react components. Add more as needed.
const ICONS = { Sparkles, Dumbbell, Ticket, Star, Zap };

// ---------------------------------------------------------------------------
// OfferModal — pricing-style "choose your option" modal shown when a visitor
// clicks a location's hero/navbar offer CTA (e.g. Ebbsfleet "Get Started").
// Options render as side-by-side pricing cards, each with an icon, note,
// description and a "Choose" button that opens its URL in a new tab.
//
// Props:
//   offer   - { title, intro, options: [{ icon, label, note, description,
//               cta, highlight, badge, url }] }
//   onClose - called to dismiss the modal
// ---------------------------------------------------------------------------
const OfferModal = ({ offer, onClose }) => {
	// Close on Escape and lock body scroll while open.
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handleKeyDown);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = previousOverflow;
		};
	}, [onClose]);

	if (!offer) return null;

	const options = offer.options || [];

	return (
		<div
			className="offer-modal-backdrop"
			onClick={onClose}
			role="dialog"
			aria-modal="true"
			aria-label={offer.title || "Choose your option"}
		>
			<div className="offer-modal-content" onClick={(e) => e.stopPropagation()}>
				<button
					type="button"
					className="offer-modal-close"
					onClick={onClose}
					aria-label="Close"
				>
					×
				</button>

				<img
					src={process.env.PUBLIC_URL + "/EU59_LogoWHTAsset 5@4x.png"}
					alt="Urban 59"
					className="offer-modal-logo"
				/>

				<h2 className="offer-modal-title">
					{offer.title || "Choose Your Option"}
				</h2>
				{offer.intro && <p className="offer-modal-intro">{offer.intro}</p>}

				<div className="offer-pricing-grid">
					{options.map((opt, index) => {
						const Icon = ICONS[opt.icon] || Ticket;
						return (
							<div
								key={index}
								className={`offer-card${opt.highlight ? " offer-card-featured" : ""}`}
							>
								{opt.badge && (
									<span className="offer-card-badge">{opt.badge}</span>
								)}
								<div className="offer-card-icon" aria-hidden="true">
									<Icon size={30} strokeWidth={2} />
								</div>
								<h3 className="offer-card-title">{opt.label}</h3>
								{opt.note && <p className="offer-card-note">{opt.note}</p>}
								{opt.price && (
									<p className="offer-card-price">
										<span className="offer-card-price-amount">{opt.price}</span>
										{opt.priceSub && (
											<span className="offer-card-price-sub">
												{opt.priceSub}
											</span>
										)}
									</p>
								)}
								{opt.description && (
									<p className="offer-card-desc">{opt.description}</p>
								)}
								<a
									className="offer-card-btn"
									href={opt.url}
									target="_blank"
									rel="noopener noreferrer"
									onClick={onClose}
								>
									{opt.cta || `Choose ${opt.label}`}
								</a>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default OfferModal;
